const normalizeRoleStatus = (value) => String(value || "").trim().toLowerCase();

const normalizeDate = (value) => {
  if (!value) return null;

  if (value?.toDate) {
    const date = value.toDate();
    return Number.isNaN(date?.getTime?.()) ? null : date;
  }

  if (typeof value?.seconds === "number") {
    const date = new Date(value.seconds * 1000);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      const date = new Date(Number(year), Number(month) - 1, Number(day));
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const date = new Date(trimmed);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
};

const getComparableDate = (sampleOrder) =>
  normalizeDate(
    sampleOrder?.createdAt ||
      sampleOrder?.requestedDate ||
      sampleOrder?.deliveryDate ||
      sampleOrder?.updatedAt
  );

const getSampleOrderProductIds = (sampleOrder) => {
  const items = Array.isArray(sampleOrder?.items) ? sampleOrder.items : [];
  const itemIds = items
    .map((item) => String(item?.productId || "").trim())
    .filter(Boolean);

  if (itemIds.length) return Array.from(new Set(itemIds));

  const fallbackId = String(sampleOrder?.productId || "").trim();
  return fallbackId ? [fallbackId] : [];
};

export const findDuplicateSampleRequest = ({
  sampleOrders = [],
  accountId,
  productIds = [],
  windowDays,
  excludeRequestId = null,
}) => {
  const normalizedAccountId = String(accountId || "").trim();
  const normalizedProductIds = Array.from(
    new Set(
      (Array.isArray(productIds) ? productIds : [])
        .map((id) => String(id || "").trim())
        .filter(Boolean)
    )
  );
  const days = Number(windowDays);

  if (!normalizedAccountId || !normalizedProductIds.length || !Number.isFinite(days) || days <= 0) {
    return null;
  }

  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - days);

  const excludedId = String(excludeRequestId || "").trim();

  const matches = (Array.isArray(sampleOrders) ? sampleOrders : [])
    .filter((sampleOrder) => {
      if (!sampleOrder) return false;
      if (excludedId && String(sampleOrder.id || "").trim() === excludedId) return false;
      if (String(sampleOrder.accountId || "").trim() !== normalizedAccountId) return false;

      const status = normalizeRoleStatus(sampleOrder.status);
      if (status === "cancelled") return false;

      const sampleDate = getComparableDate(sampleOrder);
      if (!sampleDate || sampleDate < cutoff) return false;

      const existingProductIds = getSampleOrderProductIds(sampleOrder);
      return existingProductIds.some((productId) => normalizedProductIds.includes(productId));
    })
    .sort((a, b) => {
      const aTime = getComparableDate(a)?.getTime?.() || 0;
      const bTime = getComparableDate(b)?.getTime?.() || 0;
      return bTime - aTime;
    });

  if (!matches.length) return null;

  const match = matches[0];
  const overlappingNames = (Array.isArray(match.items) ? match.items : [])
    .filter((item) => normalizedProductIds.includes(String(item?.productId || "").trim()))
    .map((item) => String(item?.productName || item?.name || "").trim())
    .filter(Boolean);

  return {
    match,
    duplicateItemNames: Array.from(new Set(overlappingNames)),
    cutoff,
  };
};
